/**
 * Quests — the story layer over work the student was going to do anyway.
 *
 * `docs/rpg.md` §13–14 asks for quests that are a problem the world has rather
 * than a quota of questions: the bridge is broken, the wagons carry 15 kg, how
 * many trips. So a quest is two things and nothing else — a short piece of
 * *story* and an *objective stated in the platform's own vocabulary*: master
 * these concepts, read these lessons, answer this many correctly, clear these
 * quests. There is no third thing. A quest cannot invent a question, cannot
 * grade one, and cannot hold any mathematics of its own; it names concepts and
 * lessons that already exist in the curriculum graph, and the ordinary lesson
 * and practice machinery does the teaching.
 *
 * Two rules from `docs/rpg-implementation-plan.md` §0.4 shape the rest:
 *
 * **Progress is derived, never stored.** `questProgress` is a pure function of
 * the definitions and the profile, exactly like `assignmentProgress` — there is
 * no completion flag, no "mark as done", nothing to drift out of step with what
 * the student has actually done. A quest completes itself as they learn.
 *
 * **Rewards are settled, and settled once.** The single stored fact about a
 * finished quest is that its reward was paid, and the only thing that pays it
 * is `claimQuestReward` in `packages/learning/src/rpg.ts`, idempotent by
 * `claimedQuestIds`. Nothing in this file adds XP, touches mastery, moves a
 * streak, completes a daily quest or schedules a review. It reads.
 *
 * And, as everywhere in this package: a quest is a story state, never an access
 * control. A locked quest is one whose *story* has not started. Every lesson
 * and every practice page it names stays reachable from the ordinary routes,
 * this instant, to a student who has never opened the adventure at all.
 */

import { isMastered, type LearnerProfile } from '@yuan-math/learning';

/**
 * `docs/rpg.md` §13 names seven kinds; three of them are a *definition* rather
 * than a presentation. Daily and challenge quests are the existing daily quests
 * and the existing difficulty ladder wearing a hat, exploration is PR 11's
 * journal, and a boss is not a quest at all — it has its own engine in PR 8.
 * What is left is the distinction that changes behaviour here: `main` quests
 * form a kingdom's chain, `side` and `review` stand outside it and are never
 * locked. `review` differs from `side` only in what it asks for — old work,
 * revisited — which is a fact worth having on the board and in the icon.
 */
export type QuestKind = 'main' | 'side' | 'review';

/**
 * One short paragraph of story, optionally spoken by somebody.
 *
 * Data, never components (`CLAUDE.md`: content is data). The same rule that
 * keeps lesson prose out of `apps/web` keeps quest prose out of it: one file
 * decides how a beat looks, and `content/` decides what it says.
 */
export type StoryBeat = {
  /** The villager saying it. Absent for narration. */
  speaker?: string;
  text: string;
};

/**
 * What finishing the quest means, in the platform's own vocabulary.
 *
 * Every variant is a question the profile can already answer, which is what
 * makes derived progress possible. Nothing here can express "answer 20
 * questions and we will not look at whether you got them right" — that is
 * completion-thinking, and `assignments.ts` explains at length why this
 * platform refuses it.
 */
export type QuestObjective =
  /**
   * Reach mastery on `count` of these concepts. Mastery proper — earned by
   * practice, six attempts minimum — not `knownSet`. A quest reward paid off a
   * placement *estimate* would be paying for something the student has not
   * done, and the estimate is exactly what the first real attempt clears.
   *
   * `count` may be fewer than the concepts listed, and usually should be: a
   * single concept a student happens not to have met should not hold a whole
   * chain shut (§0.6).
   */
  | { kind: 'master-concepts'; conceptIds: string[]; count: number }
  /** Read these lessons. For an introduction, before practice makes sense. */
  | { kind: 'complete-lessons'; lessonIds: string[] }
  /**
   * Answer `correctCount` questions correctly on these concepts, *after
   * accepting the quest* — see `questProgress` for why the acceptance window
   * exists and why `correctCount` is small.
   */
  | { kind: 'practice'; conceptIds: string[]; correctCount: number }
  /** Clear these quests first. A capstone; the fête at the end of the chain. */
  | { kind: 'clear-quests'; questIds: string[] };

/**
 * What clearing it pays.
 *
 * Cosmetic currency and the ordinary XP economy, and nothing else (§31, §0.2).
 * A quest never pays mastery: "+1 Algebra Mastery" in the §14 mock-up is the
 * one line of that example this platform cannot honour, because mastery is
 * measured from attempts and is not a thing anyone can be given.
 */
export type QuestReward = { xp: number; gold: number };

export type QuestDef = {
  id: string;
  kingdomId: string;
  kind: QuestKind;
  /** Fantasy name, shown on the board. "The Broken Bridge". */
  title: string;
  /** Two to four short paragraphs. Skippable, and written to be skipped. */
  story: StoryBeat[];
  objective: QuestObjective;
  reward: QuestReward;
  /**
   * Position on the kingdom's board, and — for `main` quests — the chain
   * itself: main quests of a kingdom run 0, 1, 2, … with no gaps, and quest
   * *n* is locked until quest *n − 1* has been claimed.
   */
  order: number;
};

/**
 * Where a student stands with one quest.
 *
 * `locked` — an earlier main quest in this kingdom's chain is unclaimed. Story
 * only: everything the quest names is open to them regardless.
 * `available` — offered, not yet taken on.
 * `accepted` — taken on, objective unmet.
 * `complete` — objective met, reward unpaid.
 * `claimed` — reward paid, once, by `claimQuestReward`.
 */
export type QuestState = 'locked' | 'available' | 'accepted' | 'complete' | 'claimed';

export type QuestProgress = {
  questId: string;
  state: QuestState;
  /** How much of the objective is behind them. Never above `total`. */
  done: number;
  total: number;
};

/**
 * The most correct answers a `practice` objective may ask for.
 *
 * The attempt log is bounded at `ATTEMPT_LOG_LIMIT` (400) and older attempts
 * are dropped from it, so a `practice` objective is counted against a *window*
 * rather than against the student's whole history. Ask for forty and a student
 * who does a heavy fortnight of other work between accepting and finishing can
 * watch the count go backwards as their own early answers roll out of memory —
 * a quest that un-finishes itself, and the one failure of derived progress that
 * is not honest. Ten cannot: four hundred attempts is a long way past any
 * plausible sitting, and a quest of ten is finished inside one.
 */
export const QUEST_CORRECT_LIMIT = 10;

/**
 * How far through a quest a student is. Pure, derived, nothing stored.
 *
 * `quests` is the catalogue the chain is read from — the kingdom's quests, or
 * the whole world's; it filters. It is a parameter rather than a module-level
 * table for the reason everything else in this package is: `packages/world`
 * sits below `content/`, and the definitions live there.
 */
export function questProgress(
  def: QuestDef,
  quests: readonly QuestDef[],
  profile: LearnerProfile,
  now: number,
): QuestProgress {
  const claimed = new Set(profile.rpg?.claimedQuestIds ?? []);
  const acceptedAt = profile.rpg?.questLog[def.id]?.acceptedAt;
  const total = objectiveTotal(def.objective);

  /*
   * Claimed reads full, and does not re-derive.
   *
   * This is the one place the derived-progress rule bends, deliberately.
   * Mastery decays and lessons can be un-read by nothing at all, so a claimed
   * quest re-counted six months later can come back 3/4 — a finished story
   * un-finishing itself after its reward was spent. An assignment is allowed to
   * do that, and should: it is a live standard with a deadline, and forgetting
   * it before Friday means it was not learned. A quest is a thing that happened.
   */
  if (claimed.has(def.id)) return { questId: def.id, state: 'claimed', done: total, total };

  const done = Math.min(total, objectiveDone(def.objective, profile, claimed, acceptedAt, now));

  // Locked still reports its count, because it is worth knowing that the
  // mathematics of a quest you have not reached is already half behind you.
  if (isLocked(def, quests, claimed)) return { questId: def.id, state: 'locked', done, total };
  if (acceptedAt === undefined) return { questId: def.id, state: 'available', done, total };
  return { questId: def.id, state: done >= total ? 'complete' : 'accepted', done, total };
}

/**
 * A kingdom's quests, in board order: the main chain first, then everything
 * else. The chain is the spine of the kingdom and reads top to bottom; side and
 * review quests are optional and sit under it.
 */
export function questsForKingdom(quests: readonly QuestDef[], kingdomId: string): QuestDef[] {
  return quests
    .filter((quest) => quest.kingdomId === kingdomId)
    .sort((a, b) => {
      if ((a.kind === 'main') !== (b.kind === 'main')) return a.kind === 'main' ? -1 : 1;
      if (a.order !== b.order) return a.order - b.order;
      return a.id.localeCompare(b.id);
    });
}

/**
 * The next step in a kingdom's story: the first main quest whose reward is
 * unclaimed, whether the student has accepted it yet or not. `undefined` once
 * the chain is finished.
 *
 * Unclaimed rather than incomplete on purpose — a quest sitting finished and
 * unpaid is still the next thing to do, and the thing to do about it is claim
 * it. `profile` and `now` are here because PR 10's "Continue Adventure" will
 * want to say *why* this is next, and because a caller holding a progress
 * result should never have to ask twice.
 */
export function nextMainQuest(
  quests: readonly QuestDef[],
  kingdomId: string,
  profile: LearnerProfile,
  now: number,
): { quest: QuestDef; progress: QuestProgress } | undefined {
  for (const quest of questsForKingdom(quests, kingdomId)) {
    if (quest.kind !== 'main') continue;
    const progress = questProgress(quest, quests, profile, now);
    if (progress.state !== 'claimed') return { quest, progress };
  }
  return undefined;
}

/**
 * The quest holding this one shut, or `undefined` when nothing is.
 *
 * A board that says "locked" and stops has told the student the one thing they
 * cannot act on. This is what lets it say *"finish The Lantern Count first"* —
 * and it is exported rather than re-derived in the UI so the sentence on the
 * card and the state on the chip can never come from two different rules; the
 * lock itself is `earliestBlocker` too.
 *
 * The earliest unclaimed link rather than the one immediately before, because
 * that is the one to go and do: a chain can have several unclaimed links behind
 * a quest, and naming the last of them would send the student to another locked
 * door.
 */
export function blockingQuest(
  def: QuestDef,
  quests: readonly QuestDef[],
  profile: LearnerProfile,
): QuestDef | undefined {
  return earliestBlocker(def, quests, new Set(profile.rpg?.claimedQuestIds ?? []));
}

/* -------------------------------------------------------------------------- */

/** Side and review quests are never locked; a main quest waits for its chain. */
function isLocked(def: QuestDef, quests: readonly QuestDef[], claimed: ReadonlySet<string>): boolean {
  return earliestBlocker(def, quests, claimed) !== undefined;
}

function earliestBlocker(
  def: QuestDef,
  quests: readonly QuestDef[],
  claimed: ReadonlySet<string>,
): QuestDef | undefined {
  if (def.kind !== 'main') return undefined;
  let blocker: QuestDef | undefined;
  for (const other of quests) {
    if (other.kind !== 'main' || other.kingdomId !== def.kingdomId) continue;
    if (other.order >= def.order || claimed.has(other.id)) continue;
    if (!blocker || other.order < blocker.order) blocker = other;
  }
  return blocker;
}

function objectiveTotal(objective: QuestObjective): number {
  switch (objective.kind) {
    case 'master-concepts':
      return objective.count;
    case 'complete-lessons':
      return objective.lessonIds.length;
    case 'practice':
      return objective.correctCount;
    case 'clear-quests':
      return objective.questIds.length;
  }
}

function objectiveDone(
  objective: QuestObjective,
  profile: LearnerProfile,
  claimed: ReadonlySet<string>,
  acceptedAt: number | undefined,
  now: number,
): number {
  switch (objective.kind) {
    case 'master-concepts':
      return objective.conceptIds.filter((id) => isMastered(profile.concepts[id], now)).length;

    case 'complete-lessons': {
      const read = new Set(profile.completedLessonIds);
      return objective.lessonIds.filter((id) => read.has(id)).length;
    }

    case 'practice': {
      /*
       * Counted from acceptance, so the quest is a thing the student does
       * rather than a thing they turn out to have already done. Work from
       * before they took it on was done for its own reasons and belongs to
       * whatever they were doing then.
       */
      if (acceptedAt === undefined) return 0;
      const concepts = new Set(objective.conceptIds);
      return profile.attempts.filter(
        (attempt) =>
          // `status`, not `correct`. Malformed input is not a wrong answer and
          // is not an answer at all (CLAUDE.md): an empty box or a "half" typed
          // into a quest must not move its bar, in either direction.
          attempt.status === 'correct' && attempt.at >= acceptedAt && concepts.has(attempt.conceptId),
      ).length;
    }

    case 'clear-quests':
      // Claimed, not complete: the fête is thrown for the quests that were seen
      // through to the end, and claiming is the record that they were.
      return objective.questIds.filter((id) => claimed.has(id)).length;
  }
}
